import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import authService from '../../services/authService';

const ResetPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.light};
`;

const ResetPasswordCard = styled(Card)`
  max-width: 400px;
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.gray};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.radii.small};
  font-size: ${theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.small};
  margin: ${theme.spacing.xs} 0 0 0;
`;

const SuccessMessage = styled.p`
  color: ${theme.colors.success};
  font-size: ${theme.fontSizes.medium};
  text-align: center;
  margin: ${theme.spacing.md} 0;
`;

const BackToLogin = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.small};
  
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
    margin-left: ${theme.spacing.xs};
  }
`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  
  const query = useQuery();
  const token = query.get('token');
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!token) {
      setInvalidToken(true);
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!password || !confirmPassword) {
      setError('Please enter both password fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      
      if (token) {
        await authService.confirmResetPassword({ token, password });
      } else {
        throw new Error('Reset token is missing');
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Reset password error:', err);
      const errorMessage = err.response?.data?.error || 
                           err.message || 
                           'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResetPasswordContainer>
      <ResetPasswordCard>
        <Header>
          <h1>Reset Password</h1>
          <p>Enter your new password</p>
        </Header>
        
        {invalidToken ? (
          <>
            <ErrorMessage>{error}</ErrorMessage>
            <BackToLogin>
              <Link to="/forgot-password">Request a new reset link</Link>
            </BackToLogin>
          </>
        ) : success ? (
          <>
            <SuccessMessage>
              Your password has been reset successfully! Redirecting to login...
            </SuccessMessage>
            <BackToLogin>
              <Link to="/login">Back to Login</Link>
            </BackToLogin>
          </>
        ) : (
          <>
            <Form onSubmit={handleSubmit}>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <FormGroup>
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  required
                  minLength={8}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  required
                  minLength={8}
                />
              </FormGroup>
              
              <Button type="submit" fullWidth disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Form>
            
            <BackToLogin>
              <Link to="/login">Back to Login</Link>
            </BackToLogin>
          </>
        )}
      </ResetPasswordCard>
    </ResetPasswordContainer>
  );
};

export default ResetPassword; 